// imports — add forwardRef, useImperativeHandle, useRef
import React, { useMemo, useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { MS_CONFIGS } from '../../../organisms/SecufireActionLogs/SecufireActionLogs';
import './DonutChart.css';

const XS_BREAKPOINT        = 360;  // très petit mobile (ex: 320px) — on masque le label texte
const MOBILE_BREAKPOINT    = 480;
const TABLET_BREAKPOINT    = 900;
const NARROW_SM_BREAKPOINT = 1100; // zone 900–1100 : trop étroit pour le texte central du donut
                                    // (débordait hors du trou, cf. bug à 1084px) → on le masque
                                    // entièrement pour ce palier plutôt que de réduire la police.
const NARROW_BREAKPOINT    = 1500; // desktop étroit — légende verticale à l'étroit, donut fortement réduit

// ── Palette "famille bleu" pour le mode endpoint ───────────────────────────
const ENDPOINT_PALETTE = [
  '#0EA5E9', // sky-500   — base (identique à AUTH_COLOR)
  '#0C4A6E', // sky-900   — bleu très foncé
  '#8B5CF6', // violet-500
  '#6366F1', // indigo-500
  '#0891B2', // cyan-600
  '#F59E0B', // amber-500
  '#0369A1', // sky-700   — bleu foncé
  '#EF4444', // red-500
  '#4338CA', // indigo-700 — bleu-violet foncé
  '#84CC16', // lime-500
];

const getEndpointColor = (index) => ENDPOINT_PALETTE[index % ENDPOINT_PALETTE.length];

// ── Hook responsive léger — legend/centre du donut sont du canvas ECharts,
// donc inatteignables via media queries CSS : on adapte l'`option` en JS.
// Six paliers : xs (≤360), mobile (361–480), tablet (481–900),
// narrow-sm (901–1100, texte central masqué), narrow (1101–1500, desktop
// étroit), desktop (>1500).
const useSizeTier = () => {
  const getTier = () => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    if (w <= XS_BREAKPOINT) return 'xs';
    if (w <= MOBILE_BREAKPOINT) return 'mobile';
    if (w <= TABLET_BREAKPOINT) return 'tablet';
    if (w <= NARROW_SM_BREAKPOINT) return 'narrow-sm';
    if (w <= NARROW_BREAKPOINT) return 'narrow';
    return 'desktop';
  };
  const [tier, setTier] = useState(getTier);
  useEffect(() => {
    const onResize = () => setTier(getTier());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return tier;
};

const DonutChart = forwardRef(({
  mode = 'microservice',
  microservices = [],
  selectedMsSet,
  endpoints = [],
  totalCalls,
  color = '#0EA5E9',
}, ref) => {
  const chartRef = useRef(null);
  useImperativeHandle(ref, () => ({
    getDataURL: (opts) => chartRef.current?.getEchartsInstance?.()?.getDataURL(opts),
  }));

  const tier = useSizeTier();
  const isCompact = tier !== 'desktop' && tier !== 'narrow'; // mobile, xs OU tablette

  // ── Géométrie du donut selon le palier ──────────────────────────
  // "narrow-sm" (900–1100px) : plus de texte central à loger (masqué,
  // cf. showCenterText plus bas) → pas besoin d'un grand trou vide, le
  // donut peut rester quasi plein.
  // "narrow" (1101–1500px) : le donut est fortement réduit et décalé
  // nettement à gauche pour garantir qu'aucune légende ne vienne le
  // chevaucher, quelle que soit la longueur des libellés.
  const pieRadius =
    tier === 'xs'        ? ['42%', '64%'] :
    tier === 'mobile'    ? ['40%', '62%'] :
    tier === 'tablet'    ? ['44%', '68%'] :
    tier === 'narrow-sm' ? ['0%', '62%']  :
    tier === 'narrow'    ? ['24%', '40%'] :
    ['48%', '72%'];

  const pieCenter =
    tier === 'xs'        ? ['50%', '44%'] :
    tier === 'mobile'    ? ['50%', '38%'] :
    tier === 'tablet'    ? ['50%', '40%'] :
    tier === 'narrow-sm' ? ['22%', '50%'] :
    tier === 'narrow'    ? ['20%', '50%'] :
    ['32%', '50%'];

  // Conservé même si le texte central est masqué en "narrow-sm", au cas où
  // on le réactive plus tard pour ce palier.
  const centerStyle =
    tier === 'xs'        ? { left: '50%', top: '44%' } :
    tier === 'mobile'    ? { left: '50%', top: '38%' } :
    tier === 'tablet'    ? { left: '50%', top: '40%' } :
    tier === 'narrow-sm' ? { left: '22%', top: '50%' } :
    tier === 'narrow'    ? { left: '20%', top: '50%' } :
    { left: '32%', top: '50%' };

  const legendCompactProps =
    tier === 'xs'
      ? {
          orient: 'horizontal',
          left: 'center',
          right: 'auto',
          top: 'auto',
          bottom: 2,
          type: 'scroll',
          textStyle: { fontSize: 8, color: '#475569', fontWeight: 500 },
          itemWidth: 7,
          itemHeight: 7,
        }
      : tier === 'mobile'
      ? {
          orient: 'horizontal',
          left: 'center',
          right: 'auto',
          top: 'auto',
          bottom: 4,
          type: 'scroll',
          textStyle: { fontSize: 9, color: '#475569', fontWeight: 500 },
          itemWidth: 8,
          itemHeight: 8,
        }
      : tier === 'tablet'
      ? {
          orient: 'horizontal',
          left: 'center',
          right: 'auto',
          top: 'auto',
          bottom: 6,
          type: 'scroll',
          textStyle: { fontSize: 10, color: '#475569', fontWeight: 500 },
          itemWidth: 9,
          itemHeight: 9,
          itemGap: 12,
        }
      : tier === 'narrow-sm'
      ? {
          // Le centre du donut est libéré (pas de texte à cette taille) →
          // la légende peut récupérer un peu plus de largeur qu'en "narrow".
          orient: 'vertical',
          right: 4,
          top: 'middle',
          width: 200,
          textStyle: { fontSize: 10, color: '#475569', fontWeight: 500 },
          itemWidth: 8,
          itemHeight: 8,
        }
      : tier === 'narrow'
      ? {
          // ── Donut très réduit ci-dessus → large colonne disponible pour
          // la légende, texte affiché en entier, sans troncature ni
          // chevauchement.
          orient: 'vertical',
          right: 6,
          top: 'middle',
          width: 260,
          textStyle: { fontSize: 10.5, color: '#475569', fontWeight: 500 },
          itemWidth: 9,
          itemHeight: 9,
        }
      : {
          orient: 'vertical',
          right: 8,
          top: 'middle',
          textStyle: { fontSize: 11, color: '#475569', fontWeight: 500 },
          itemWidth: 10,
          itemHeight: 10,
        };

  /* ══ MODE MICROSERVICE ══════════════════════════════════════════ */
  const msMemo = useMemo(() => {
    if (mode !== 'microservice') return null;
    const allMs = microservices.filter((m) => (m.total_usage || 0) > 0);
    const total = allMs.reduce((s, m) => s + (m.total_usage || 0), 0);
    if (!allMs.length) return null;

    const seriesData = allMs.map((m) => ({
      name:  MS_CONFIGS[m.microservice]?.label || m.microservice,
      value: m.total_usage || 0,
      itemStyle: {
        color:   MS_CONFIGS[m.microservice]?.color || '#64748b',
        opacity: selectedMsSet.has(m.microservice) ? 1 : 0.22,
      },
    }));

    return {
      total,
      option: {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255,255,255,0.97)',
          borderColor: '#e2e8f0', borderWidth: 1,
          textStyle: { color: '#1e293b', fontSize: 11 },
          formatter: (p) => {
            const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
            return `<div style="font-weight:700;margin-bottom:4px">${p.name}</div>
              <div style="display:flex;align-items:center;gap:8px">
                <span style="color:#64748b">${p.value.toLocaleString()} appels</span>
                <strong style="margin-left:auto;color:${p.color}">${pct}%</strong>
              </div>`;
          },
        },
        legend: {
          icon: 'circle',
          formatter: (name) => {
            const ms  = allMs.find((m) => (MS_CONFIGS[m.microservice]?.label || m.microservice) === name);
            const pct = ms && total > 0 ? ((ms.total_usage / total) * 100).toFixed(0) : 0;
            return `${name}  ${pct}%`;
          },
          ...legendCompactProps,
        },
        series: [{
          type: 'pie', radius: pieRadius, center: pieCenter,
          avoidLabelOverlap: true,
          label: { show: false }, labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 6, label: { show: false } },
          data: seriesData,
        }],
      },
    };
  }, [mode, microservices, selectedMsSet, tier]);

  /* ══ MODE ENDPOINT ══════════════════════════════════════════════ */
  const epMemo = useMemo(() => {
    if (mode !== 'endpoint') return null;
    const allEp = endpoints.filter((e) => (e.total_usage || 0) > 0);
    const total = allEp.reduce((s, e) => s + (e.total_usage || 0), 0);
    if (!allEp.length) return null;

    const seriesData = allEp.map((e, i) => ({
      name:  e.endpoint || e.functionality || `ep${i}`,
      value: e.total_usage || 0,
      itemStyle: { color: getEndpointColor(i) },
    }));

    return {
      total,
      option: {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255,255,255,0.97)',
          borderColor: '#e2e8f0', borderWidth: 1,
          textStyle: { color: '#1e293b', fontSize: 11 },
          formatter: (p) => {
            const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
            return `<div style="font-weight:700;margin-bottom:4px">${p.name}</div>
              <div style="display:flex;align-items:center;gap:8px">
                <span style="color:#64748b">${p.value.toLocaleString()} appels</span>
                <strong style="margin-left:auto;color:${p.color}">${pct}%</strong>
              </div>`;
          },
        },
        legend: {
          icon: 'circle',
          formatter: (name) => {
            const ep  = allEp.find((e) => (e.endpoint || e.functionality || '') === name);
            const pct = ep && total > 0 ? ((ep.total_usage / total) * 100).toFixed(0) : 0;
            return `${name}  ${pct}%`;
          },
          ...legendCompactProps,
          textStyle: {
            ...legendCompactProps.textStyle,
            fontSize:
              tier === 'xs' ? 8 :
              tier === 'mobile' ? 8 :
              tier === 'tablet' ? 10 :
              tier === 'narrow-sm' ? 10 :
              tier === 'narrow' ? 10 : 10,
          },
        },
        series: [{
          type: 'pie', radius: pieRadius, center: pieCenter,
          avoidLabelOverlap: true,
          label: { show: false }, labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 6, label: { show: false } },
          data: seriesData,
        }],
      },
    };
  }, [mode, endpoints, tier]);

  /* ══ RENDER ═════════════════════════════════════════════════════ */
  const memo  = mode === 'endpoint' ? epMemo : msMemo;

  if (!memo) return (
    <div className="donut-chart donut-chart--empty">
      <span className="donut-chart__empty-text">Aucune donnée</span>
    </div>
  );

  // Texte central masqué entre 900 et 1100px ("narrow-sm") : le trou du
  // donut y est trop petit pour accueillir le total + le libellé sans
  // déborder, quelle que soit la taille de police choisie.
  const showCenterText = tier !== 'narrow-sm';
  // "appels total" reste en plus masqué sur très petit mobile (≤360px)
  const showTotalLabel = tier !== 'xs';

  return (
    <div className="donut-chart">
      <ReactECharts
        ref={chartRef}
        option={memo.option}
        style={{ height: '100%', width: '100%' }}
        notMerge={true}
      />
      {showCenterText && (
        <div className="donut-chart__center" style={{ left: centerStyle.left, top: centerStyle.top }}>
          <span
            className="donut-chart__total"
            style={{
              fontSize:
                tier === 'desktop' ? 20 :
                tier === 'narrow'  ? 15 :
                tier === 'tablet'  ? 17 :
                tier === 'mobile'  ? 15 : 16,
            }}
          >
            {memo.total.toLocaleString()}
          </span>
          {showTotalLabel && (
            <span className="donut-chart__label" style={{ fontSize: tier === 'desktop' ? 10 : tier === 'narrow' ? 8 : 9 }}>
              appels total
            </span>
          )}
        </div>
      )}
    </div>
  );
});

export default DonutChart;